import { useCallback, useRef, useState } from "react";
import type { ApplyBrandOptions, ApplyBrandResult, BrandSpec } from "../../../types";
import { applyBrandToSlide, applyBrandToDeck } from "../services/brandApplier";
import { logError, scrubMessage } from "../../../../../telemetry";

export type ApplyState =
  | { status: "idle" }
  | { status: "applying"; message: string; current: number; total: number }
  | { status: "done"; result: ApplyBrandResult }
  | { status: "error"; message: string };

/** connectorSystemName tag for Deckorate apply-brand telemetry. */
const APPLY_BRAND_CONNECTOR = "deckorate";

/** Report an apply-brand failure through the shared telemetry pipeline (PII-scrubbed). */
function logApplyBrandError(reason: string, type: string, message: string): void {
  logError(
    "error.connector.failure",
    { type, scrubbedMessage: scrubMessage(message) },
    { connectorSystemName: APPLY_BRAND_CONNECTOR, reason },
  );
}

export function useBrandApply() {
  const [state, setState] = useState<ApplyState>({ status: "idle" });
  // Guards against a second apply starting while one is already in flight
  // (e.g. a fast double-click before the "applying" state disables the
  // button) — without this, both PowerPoint.run batches would race.
  const isApplyingRef = useRef(false);

  const apply = useCallback(async (brand: BrandSpec, options: Partial<ApplyBrandOptions>) => {
    if (isApplyingRef.current) return;
    isApplyingRef.current = true;

    // total starts at 1 (not 0) purely so the initial 0/1 ratio still renders
    // a 0% progress bar immediately; the first onProgress call below replaces
    // it with the real slide count.
    setState({ status: "applying", message: "Starting…", current: 0, total: 1 });

    const opts: Partial<ApplyBrandOptions> = {
      ...options,
      onProgress: (message: string, current: number, total: number) => {
        setState({ status: "applying", message, current, total });
      },
    };

    try {
      const result =
        options.scope === "deck"
          ? await applyBrandToDeck(brand, opts)
          : await applyBrandToSlide(brand, opts);

      setState({ status: "done", result });
    } catch (e) {
      const message = e instanceof Error ? e.message : "Apply brand failed";
      logApplyBrandError(`brand-apply-${options.scope ?? "slide"}`, "ApplyBrandError", message);
      setState({ status: "error", message });
    } finally {
      isApplyingRef.current = false;
    }
  }, []);

  const reset = useCallback(() => setState({ status: "idle" }), []);

  return { state, apply, reset };
}
